import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React from 'react';
import { useTranslation } from 'react-i18next';

const getFitScoreTone = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'danger';
};

const normalizeFitScore = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isInteger(numeric)) return null;
  return Math.max(0, Math.min(100, numeric));
};

const ApplicantCell = ({ applicant, onDragStart, onClick, isHired, isInProgress, isSelected, isStarred, isOfferApprover, pendingApprovals, applicantStatus = {}, substageType = 'static', substageAssessmentId = null, neverOpened = false }) => {
  useUiLanguage();
  const { t, i18n } = useTranslation('applicants');
  const isRTL = i18n.dir(i18n.language) === 'rtl';
  const handleDragStart = (e) => {
    e.dataTransfer.setData("text/plain", applicant.id);
    if (onDragStart) {
      onDragStart(e, applicant.id);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick(applicant.id);
    }
  };

  // Split name into first and last name for display
  const fullName = applicant.name || applicant.username || 'Unknown';
  const nameParts = fullName.split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  // Determine if applicant is in progress (has current icon)
  const showCurrentIcon = applicant.currentPosition || isInProgress;
  const fitScore = normalizeFitScore(applicant.fit_score);
  const fitScoreTone = fitScore === null ? null : getFitScoreTone(fitScore);

  const { assessmentsByStep = {}, interviewScheduled, interviewComplete } = applicantStatus;

  const isInterviewStep = substageType === 'interview';
  const isAssessmentStep = substageType === 'assessment' || substageType === 'ai-assessment' || substageType === 'ai-interview';

  // Look up assessment status only for this specific step's assessmentId
  const thisStepAssessmentStatus = isAssessmentStep && substageAssessmentId
    ? assessmentsByStep[substageAssessmentId] || null
    : null;

  const formatInterviewDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString(i18n.language || undefined, { month: 'numeric', day: 'numeric' });
    } catch (_) {
      return dateStr;
    }
  };

  const statusBadges = [];

  // Assessment badges — only on assessment steps, only for this step's specific assessment
  if (isAssessmentStep) {
    if (thisStepAssessmentStatus === 'available') {
      statusBadges.push({
        key: 'results',
        icon: 'fa-solid fa-circle-check',
        label: t('cellStatus.resultsAvailable'),
        bg: '#28a745',
      });
    } else {
      // Being placed in an assessment step means the assessment was sent
      statusBadges.push({
        key: 'awaiting',
        icon: 'fa-solid fa-hourglass-half',
        label: t('cellStatus.awaitingResults'),
        bg: '#EB8D40',
      });
    }
  }

  // Interview badges — only on interview steps
  if (isInterviewStep) {
    if (interviewScheduled) {
      statusBadges.push({
        key: 'interview-scheduled',
        icon: 'fa-solid fa-calendar-check',
        label: t('cellStatus.interviewScheduled', { date: formatInterviewDate(interviewScheduled.date) }),
        bg: '#EB8D40',
      });
    } else if (interviewComplete) {
      statusBadges.push({
        key: 'interview-done',
        icon: 'fa-solid fa-comments',
        label: t('cellStatus.interviewComplete'),
        bg: '#28a745',
      });
    }
  }

  // "Never opened" — shown on any step
  if (neverOpened) {
    statusBadges.push({
      key: 'never-opened',
      icon: 'fa-regular fa-eye-slash',
      label: t('cellStatus.neverOpened'),
      bg: '#EB8D40',
    });
  }

  return (
    <div
      className={`applicant-cell ${isRTL ? 'applicant-cell-rtl' : ''} ${isInProgress ? 'applicant-in-progress' : ''} ${isSelected ? 'applicant-selected' : ''} ${fitScore !== null ? 'applicant-has-fit-score' : ''}`}
      data-applicant-id={applicant.id}
      data-hired={isHired ? 'true' : 'false'}
      onClick={handleClick}
      draggable={true}
      onDragStart={handleDragStart}
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ cursor: 'pointer', position: 'relative' }}
    >

      {/* Star icon for starred applicants */}
      {isStarred && (
        <div style={{
          position: 'absolute',
          top: '4px',
          [isRTL ? 'left' : 'right']: '4px',
          fontSize: '12px',
          color: 'var(--app-tone-ffc107, #ffc107)',
          zIndex: 10
        }}>
          <i className="fa-solid fa-star"></i>
        </div>
      )}

      <div className="applicant-name">
        <p>{firstName}</p>
        <p>{lastName}</p>

        {/* Offer approval badge */}
        {isOfferApprover && pendingApprovals && pendingApprovals.some(p =>
          p.type === 'offer_letter' &&
          (p.entityId === applicant.id || p.metadata?.applicants?.some(a => a.id === applicant.id))
        ) && (
            <div
              className="applicant-status-badge"
              style={{ '--applicant-status-color': 'var(--app-tone-eb8d40, #EB8D40)' }}
              title={t('cellStatus.offerAwaitingApproval')}
            >
              <i className="fa-solid fa-triangle-exclamation"></i>
              <span>{t('cellStatus.awaitingOfferApproval')}</span>
            </div>
          )}

        {/* Dynamic status badges */}
        {statusBadges.length > 0 && (
          <div className="applicant-status-stack">
            {statusBadges.map(badge => (
              <div
                key={badge.key}
                className="applicant-status-badge"
                title={uiText(badge.label)}
                style={{ '--applicant-status-color': badge.bg }}
              >
                <i className={badge.icon}></i>
                <span>{uiText(badge.label)}</span>
              </div>
            ))}
          </div>
        )}

        {fitScore !== null && (
          <div
            className={`applicant-fit-rating applicant-fit-rating-${fitScoreTone}`}
            title={t('cellStatus.scoreTitle', { score: fitScore })}
            style={{ '--fit-score': fitScore }}
          >
            <div className="applicant-fit-gauge">
              <strong>{fitScore}</strong>
            </div>
            <span className="applicant-fit-rating-label">{t('cellStatus.score')}</span>
          </div>
        )}
      </div>

      {showCurrentIcon && (
        <div className="current-icon">
          <i className="fa-solid fa-location-dot" style={{ color: '#568ded' }}></i>
        </div>
      )}
    </div>
  );
};

export default ApplicantCell; 
